'use client';

import * as React from 'react';
import Image from 'next/image';
import { ArrowUp } from 'lucide-react';
import { motion, useReducedMotion } from 'motion/react';

import { PLAYS_CONTENT, type LandingPlay } from '~/lib/landing-content';
import { openDemoSection, type DemoSection } from './pipeline-live-demo';

// The plays as one story you scroll through (Abu, Oct 30), in place of the
// card grid. The rail is the three jobs from the hero; each job lists its
// live plays from PLAYS_CONTENT, so this and the card grid stay in sync.
// Every still is the live demo's own page, except Conference outreach.
const PIPELINE_STEPS_HEADER = {
  eyebrow: 'How it works',
  headline: 'Get known. Get in. Keep deals warm.',
  subtitle:
    'Not every signal is a reason to reach out. Lumnis tells you which ones are, and what the rest say about the account.'
} as const;

/**
 * Plays with a page in the live demo: a still of that page (captured from
 * the demo) and the section "See it live" opens.
 */
export const PLAY_SCREENS: Record<
  string,
  { demo: DemoSection; image: string; page: string; alt: string }
> = {
  'Account prioritization': {
    demo: 'monitors',
    image: '/assets/how-it-works/prioritization.jpg',
    page: 'Monitors',
    alt: 'A monitor ranking accounts, with Pivot marked Act now and the reason behind it'
  },
  // Outreach shows as the campaign it runs, so the page shows Lumnis sends
  // without saying so (Abu, Oct 30).
  'Top-account outreach': {
    demo: 'campaigns',
    image: '/assets/how-it-works/campaign.jpg',
    page: 'Campaigns',
    alt: 'A campaign funnel from connection requests to meetings booked, beside its recent messages'
  },
  // Browserbase's AIE NYC 2026 web-list search (120 people), names replaced
  // (Abu, Oct 30).
  'Conference outreach': {
    demo: 'projects',
    image: '/assets/how-it-works/aie-attendees.jpg',
    page: 'Prospecting',
    alt: 'Senior AI builders going to AI Engineer NYC 2026, each with fit, intent and their account in HubSpot'
  },
  'Account progression': {
    demo: 'accounts',
    image: '/assets/how-it-works/progression.jpg',
    page: 'Accounts',
    alt: 'An account page with its assessment, score and the open deal from HubSpot'
  },
  'Person research': {
    demo: 'person',
    image: '/assets/how-it-works/person.jpg',
    page: 'Person research',
    alt: 'A person report: who they are, what they care about and who they engage with most'
  },
  'Scale your best-seller’s judgment': {
    demo: 'approvals',
    image: '/assets/how-it-works/playbook-positioning.jpg',
    page: 'Playbook',
    alt: 'A learned playbook: how to position, with the reply and meeting counts behind it'
  },
  'Audience building': {
    demo: 'audience',
    image: '/assets/how-it-works/audience.jpg',
    page: 'Audience research',
    alt: 'An audience brief showing what the audience cares about and who drove it'
  },
  "What's trending": {
    demo: 'audience',
    image: '/assets/how-it-works/theme-grid.jpg',
    page: 'Audience research',
    alt: 'A grid of topics by share of audience engagement each week, marked rising, flat or spiked, with takeaways below'
  },
  'Competitor traction': {
    demo: 'audience',
    image: '/assets/how-it-works/competitor-themes.jpg',
    page: 'Audience research',
    alt: 'A table of the topics competitors post about, with posts, share and LinkedIn reactions per week'
  }
};

/**
 * Pages that are not plays of their own but show one (Abu, Oct 30), listed
 * right after the play they belong to.
 */
const EXTRA_ROWS: Record<string, LandingPlay[]> = {
  'Account progression': [
    {
      name: 'Person research',
      description:
        'Before the call, a report on the person: who they are, what they care about and who they engage with most.'
    }
  ],
  'Audience building': [
    {
      name: "What's trending",
      description:
        'Which topics your buyers engage with most, week by week, which ones are rising, and what to post about next.'
    },
    {
      name: 'Competitor traction',
      description:
        'Which topics your competitors post about, how often, and how much engagement each one gets week by week.'
    }
  ]
};

/**
 * Rows shown first in their job here only, so each job opens on a page the
 * live demo above does not already lead with (Abu, Oct 30). The card grid
 * keeps the PLAYS_CONTENT order.
 */
const LEAD_ROWS = new Set([
  'Conference outreach',
  'Person research',
  "What's trending"
]);

/**
 * The three jobs as the three moments of a warm deal (Abu, Nov 1), in the
 * order an AE lives them: known before reaching out, in the door, then the
 * live deal.
 */
const MOMENTS: Record<string, { title: string; line: string; order: number }> =
  {
    'Grow audience': {
      title: 'Get known',
      line: 'See what your buyers care about and post on it, so they know you before you reach out.',
      order: 0
    },
    'Build pipeline': {
      title: 'Get in',
      line: 'Know which accounts to work this week, who to reach and the best way in.',
      order: 1
    },
    'Progress accounts': {
      title: 'Keep deals warm',
      line: 'See who’s helping, who’s blocking and who’s engaging with competitors, so you multi-thread before the deal stalls.',
      order: 2
    }
  };

const JOBS = [...PLAYS_CONTENT.groups]
  .sort(
    (a, b) => (MOMENTS[a.title]?.order ?? 9) - (MOMENTS[b.title]?.order ?? 9)
  )
  .map((group) => {
  const plays: LandingPlay[] = group.plays
    .filter((play: LandingPlay) => !play.inDevelopment)
    .flatMap((play: LandingPlay) => [play, ...(EXTRA_ROWS[play.name] ?? [])])
    .sort(
      (a: LandingPlay, b: LandingPlay) =>
        Number(LEAD_ROWS.has(b.name)) - Number(LEAD_ROWS.has(a.name))
    );
  return {
    title: MOMENTS[group.title]?.title ?? group.title,
    line: MOMENTS[group.title]?.line ?? '',
    plays,
    firstScreen: plays.find((play) => PLAY_SCREENS[play.name])?.name ?? ''
  };
});

function JobScreen({ playName }: { playName: string }): React.JSX.Element {
  const reduceMotion = useReducedMotion();
  const screen = PLAY_SCREENS[playName];
  return (
    <div
      className="mt-6 rounded-2xl p-2 sm:p-3 md:p-4"
      style={{ background: 'color-mix(in srgb, #00C896 9%, var(--paper))' }}
    >
      <button
        type="button"
        onClick={() => openDemoSection(screen.demo)}
        className="group marketing-focus block w-full overflow-hidden rounded-xl border border-[color:var(--hairline)] bg-[color:var(--paper)] text-left shadow-sm"
      >
        <span className="flex items-center gap-1.5 border-b border-[color:var(--hairline)] px-3 py-2">
          <span className="size-2 rounded-full bg-[color:var(--hairline)]" />
          <span className="size-2 rounded-full bg-[color:var(--hairline)]" />
          <span className="size-2 rounded-full bg-[color:var(--hairline)]" />
          <span className="ml-2 font-mono text-[11px] uppercase tracking-[0.08em] text-[color:var(--ink-4)]">
            {screen.page}
          </span>
        </span>
        <span className="relative block aspect-[8/5]">
          {/* The new still fades in over nothing: no exit animation to wait
              on, so a swap never leaves the old still behind. */}
          <motion.span
            key={playName}
            className="absolute inset-0"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: reduceMotion ? 0 : 0.25 }}
          >
            <Image
              src={screen.image}
              alt={screen.alt}
              fill
              sizes="(min-width: 1024px) 820px, 100vw"
              className="object-cover object-top"
            />
          </motion.span>
        </span>
        <span className="flex items-center justify-center gap-1 border-t border-[color:var(--hairline)] px-4 py-3 text-[13px] font-medium text-[color:var(--accent-darker)] md:justify-start">
          See {playName.toLowerCase()} live
          <ArrowUp
            aria-hidden
            className="size-3.5 transition-transform group-hover:-translate-y-0.5"
          />
        </span>
      </button>
    </div>
  );
}

function JobStep({
  job,
  index,
  stepRef
}: {
  job: (typeof JOBS)[number];
  index: number;
  stepRef: (el: HTMLElement | null) => void;
}): React.JSX.Element {
  // The play whose still is showing; plays without a demo page keep the
  // current still.
  const [selected, setSelected] = React.useState(job.firstScreen);

  return (
    <article
      ref={stepRef}
      className="scroll-mt-28 text-center md:text-left"
    >
      <span
        className="inline-flex items-center gap-2 rounded-lg px-3 py-1.5 font-mono text-[13px] text-[color:var(--accent-darker)]"
        style={{ background: 'color-mix(in srgb, #00C896 10%, var(--paper))' }}
      >
        {String(index + 1).padStart(2, '0')}
      </span>
      <h3 className="mt-4 text-3xl font-medium tracking-tight text-[color:var(--ink)] md:text-4xl">
        {job.title}
      </h3>
      {job.line && (
        <p className="mx-auto mt-3 max-w-2xl text-[17px] leading-relaxed text-[color:var(--ink-3)] [text-wrap:pretty] md:mx-0">
          {job.line}
        </p>
      )}

      {selected && <JobScreen playName={selected} />}

      <ul className="mt-6 divide-y divide-[color:var(--hairline)] border-y border-[color:var(--hairline)] text-left">
        {job.plays.map((play) => {
          const hasScreen = Boolean(PLAY_SCREENS[play.name]);
          const isSelected = hasScreen && selected === play.name;
          const body = (
            <>
              <span
                className={`block text-[16px] font-medium transition-colors ${
                  isSelected
                    ? 'text-[color:var(--accent-darker)]'
                    : 'text-[color:var(--ink)]'
                }`}
              >
                {play.name}
              </span>
              <span className="mt-1 block text-[15px] leading-relaxed text-[color:var(--ink-3)] [text-wrap:pretty]">
                {play.description}
              </span>
            </>
          );
          return (
            <li key={play.name}>
              {hasScreen ? (
                <button
                  type="button"
                  aria-pressed={isSelected}
                  onClick={() => setSelected(play.name)}
                  onMouseEnter={() => setSelected(play.name)}
                  className={`marketing-focus block w-full border-l-2 py-4 pl-4 text-left transition-colors ${
                    isSelected
                      ? 'border-[color:var(--accent-darker)]'
                      : 'border-transparent hover:border-[color:var(--hairline)]'
                  }`}
                >
                  {body}
                </button>
              ) : (
                <div className="border-l-2 border-transparent py-4 pl-4">
                  {body}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </article>
  );
}

export function PipelineStepsSection(): React.JSX.Element {
  const [active, setActive] = React.useState(0);
  const stepRefs = React.useRef<(HTMLElement | null)[]>([]);

  // The job crossing the middle of the screen is the one in view.
  React.useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const index = stepRefs.current.indexOf(entry.target as HTMLElement);
          if (index >= 0) setActive(index);
        }
      },
      { rootMargin: '-45% 0px -45% 0px' }
    );
    for (const el of stepRefs.current) if (el) observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="plays"
      aria-labelledby="plays-title"
      className="mx-auto w-full max-w-[1180px] px-4 py-16 sm:px-6 md:py-24"
    >
      <header className="mx-auto max-w-3xl text-center">
        <span className="eyebrow mb-3 block">
          {PIPELINE_STEPS_HEADER.eyebrow}
        </span>
        <h2
          id="plays-title"
          className="display-h2 text-3xl text-[color:var(--ink)] [text-wrap:balance] md:text-4xl lg:text-5xl"
        >
          {PIPELINE_STEPS_HEADER.headline}
        </h2>
        <p className="mx-auto mt-4 max-w-2xl text-[16px] leading-relaxed text-[color:var(--ink-3)] [text-wrap:pretty]">
          {PIPELINE_STEPS_HEADER.subtitle}
        </p>
      </header>

      <div className="mt-12 md:mt-20 md:grid md:grid-cols-[200px_1fr] md:gap-10 lg:grid-cols-[250px_1fr] lg:gap-16">
        <nav
          aria-label="Jobs"
          className="hidden md:block"
        >
          <ol className="sticky top-28 flex flex-col">
            {JOBS.map((job, index) => (
              <li key={job.title}>
                <button
                  type="button"
                  aria-current={active === index ? 'step' : undefined}
                  onClick={() =>
                    stepRefs.current[index]?.scrollIntoView({
                      behavior: 'smooth',
                      block: 'start'
                    })
                  }
                  className={`marketing-focus flex w-full items-baseline gap-3 border-b py-4 text-left transition-colors ${
                    active === index
                      ? 'border-[color:var(--accent-darker)] text-[color:var(--accent-darker)]'
                      : 'border-[color:var(--hairline)] text-[color:var(--ink-4)] hover:text-[color:var(--ink-3)]'
                  }`}
                >
                  <span className="font-mono text-[12px]">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <span className="text-xl font-medium tracking-tight lg:text-2xl">
                    {job.title}
                  </span>
                </button>
              </li>
            ))}
          </ol>
        </nav>

        <div className="flex flex-col gap-20 md:gap-28 md:border-l md:border-dashed md:border-[color:var(--hairline)] md:pl-10 lg:pl-16">
          {JOBS.map((job, index) => (
            <JobStep
              key={job.title}
              job={job}
              index={index}
              stepRef={(el) => {
                stepRefs.current[index] = el;
              }}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
